import React from 'react';
import { Box, Container, Typography, Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  return (
    <Box sx={{ backgroundColor: '#FAF5EA', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <MuiLink component={Link} to="/" sx={{ color: '#F78764', fontWeight: 600 }}>
            ← Back to Home
          </MuiLink>
        </Box>

        <Box sx={{ backgroundColor: 'white', borderRadius: 3, p: { xs: 3, md: 5 }, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
            Terms of Service
          </Typography>
          <Typography variant="body2" sx={{ color: '#7f8c8d', mb: 4 }}>
            Last Updated: November 29, 2025
          </Typography>

          <Section title="1. Agreement to Terms">
            <Typography paragraph>
              Welcome to Snackd! These Terms of Service ("Terms") govern your access to and use of the Snackd mobile application and website (collectively, the "Service"), operated by Snackd, LLC ("Snackd," "we," "our," or "us").
            </Typography>
            <Typography paragraph sx={{ fontWeight: 600 }}>
              By accessing or using our Service, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Service.
            </Typography>
            <Typography paragraph>
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the Service. Your continued use of the Service after changes are posted constitutes your acceptance of the modified Terms.
            </Typography>
          </Section>

          <Section title="2. Description of Service">
            <Typography paragraph>
              Snackd is an AI-powered meal planning application designed to help families create personalized weekly meal plans that accommodate dietary preferences, restrictions, allergies, and picky eaters.
            </Typography>
            <Typography paragraph><strong>Our Service includes:</strong></Typography>
            <ul>
              <li>AI-generated personalized meal plans</li>
              <li>Recipe recommendations and step-by-step cooking instructions</li>
              <li>Shopping list generation</li>
              <li>Recipe library and favorites management</li>
              <li>Dietary restriction and allergy management</li>
              <li>Family member profile management</li>
            </ul>
            <Typography paragraph sx={{ fontWeight: 600 }}>
              The Service is provided "as is" and we make no warranties regarding meal plans, recipes, or nutritional information.
            </Typography>
          </Section>

          <Section title="3. Eligibility">
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>3.1 Age Requirement</Typography>
            <Typography paragraph>
              You must be at least 18 years old to create an account and use our Service. By using Snackd, you represent and warrant that you are at least 18 years of age.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>3.2 Account Registration</Typography>
            <Typography paragraph>To access certain features, you must register for an account. You agree to:</Typography>
            <ul>
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your password and account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
            </ul>
            <Typography paragraph>We reserve the right to suspend or terminate accounts that violate these Terms.</Typography>
          </Section>

          <Section title="4. Subscription Plans and Payments">
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>4.1 Free Trial</Typography>
            <Typography paragraph>
              We may offer a free trial period (typically 7-14 days) for new users. During the free trial, you have access to all features of your selected plan. If you do not cancel before the trial ends, you will automatically be charged for a subscription.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>4.2 Subscription Plans</Typography>
            <Typography paragraph>Snackd offers the following subscription plans:</Typography>
            <ul>
              <li><strong>Duo Plan:</strong> $5.00/month - For 1-2 people</li>
              <li><strong>Family Plan:</strong> $8.00/month - For up to 5 family members</li>
            </ul>
            <Typography paragraph>Prices are subject to change with 30 days' notice.</Typography>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>4.3 Billing</Typography>
            <ul>
              <li>Subscriptions automatically renew monthly unless canceled</li>
              <li>Payment is processed through Stripe, our payment processor</li>
              <li>All fees are in U.S. dollars and are non-refundable except as required by law</li>
            </ul>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>4.4 Cancellation and Refunds</Typography>
            <Typography paragraph>
              You may cancel your subscription at any time through account settings. Cancellation takes effect at the end of your current billing period. No partial refunds for unused time in the current billing period.
            </Typography>
          </Section>

          <Section title="5. Acceptable Use">
            <Typography paragraph>
              You may use the Service only for lawful purposes and in accordance with these Terms. You agree to use the Service only for personal, non-commercial purposes.
            </Typography>
            <Typography paragraph><strong>You agree NOT to:</strong></Typography>
            <ul>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights of others</li>
              <li>Upload viruses, malware, or other malicious code</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Reverse engineer, decompile, or disassemble the Service</li>
              <li>Use automated systems (bots, scrapers) to access the Service</li>
              <li>Interfere with or disrupt the Service or servers</li>
            </ul>
          </Section>

          <Section title="6. Intellectual Property Rights">
            <Typography paragraph>
              The Service and its original content (excluding user content), features, and functionality are owned by Snackd, LLC and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </Typography>
            <Typography paragraph>
              You retain ownership of any content you submit to the Service (feedback, recipe modifications, comments). By submitting content, you grant Snackd a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display your content for the purpose of operating and improving the Service.
            </Typography>
          </Section>

          <Section title="7. Third-Party Services">
            <Typography paragraph>
              Our Service may contain links to third-party websites, services, or content. We do not endorse, control, or assume responsibility for third-party content or services.
            </Typography>
            <Typography paragraph>
              Our Service uses Anthropic's Claude AI technology to generate meal plans and recipes. By using our Service, you acknowledge that AI-generated content may occasionally contain errors or inaccuracies.
            </Typography>
          </Section>

          <Section title="8. Disclaimers and Limitations of Liability">
            <Box sx={{ backgroundColor: '#fff3cd', p: 2, borderRadius: 2, mb: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>IMPORTANT: Snackd is not a substitute for professional medical or nutritional advice.</Typography>
              <Typography>
                You should consult healthcare professionals before making dietary changes. We are not responsible for adverse health effects from using our meal plans. You are solely responsible for verifying allergen information and dietary restrictions.
              </Typography>
            </Box>
            <Typography paragraph sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.
            </Typography>
            <Typography paragraph>
              IN NO EVENT SHALL SNACKD'S TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID TO SNACKD IN THE 12 MONTHS PRECEDING THE CLAIM, OR $100, WHICHEVER IS GREATER.
            </Typography>
          </Section>

          <Section title="9. Indemnification">
            <Typography paragraph>
              You agree to indemnify, defend, and hold harmless Snackd, LLC, its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, costs, or expenses arising from your use or misuse of the Service, your violation of these Terms, or your reliance on AI-generated meal plans or recipes.
            </Typography>
          </Section>

          <Section title="10. Privacy">
            <Typography paragraph>
              Your use of the Service is subject to our <MuiLink component={Link} to="/privacy">Privacy Policy</MuiLink>, which is incorporated into these Terms by reference.
            </Typography>
          </Section>

          <Section title="11. Termination">
            <Typography paragraph>
              You may terminate your account at any time by using the "Delete Account" feature in account settings or by contacting support@snackdapp.com.
            </Typography>
            <Typography paragraph>
              We may suspend or terminate your account immediately, without prior notice, if you violate these Terms, engage in fraudulent activity, or your payment method fails repeatedly.
            </Typography>
          </Section>

          <Section title="12. Dispute Resolution">
            <Typography paragraph>
              These Terms are governed by and construed in accordance with the laws of the State of Connecticut, United States, without regard to conflict of law principles.
            </Typography>
            <Typography paragraph sx={{ fontWeight: 600 }}>
              You and Snackd agree to resolve disputes through binding arbitration instead of court, except for small claims court disputes (under $10,000) and intellectual property disputes.
            </Typography>
            <Typography paragraph sx={{ fontWeight: 600 }}>
              Class Action Waiver: You agree that disputes will be resolved on an individual basis only. You waive any right to participate in a class action lawsuit or class-wide arbitration.
            </Typography>
          </Section>

          <Section title="13. Charitable Giving">
            <Typography paragraph>
              Snackd donates a portion of subscription revenue to charitable organizations focused on food security and childhood nutrition.
            </Typography>
          </Section>

          <Section title="14. Contact Information">
            <Typography paragraph>
              If you have any questions about these Terms, please contact us:
            </Typography>
            <Typography paragraph>
              <strong>Email:</strong> support@snackdapp.com
            </Typography>
            <Typography paragraph>
              <strong>Mail:</strong><br />
              Snackd, LLC<br />
              c/o Republic Registered Agent LLC<br />
              36 Russ St, 3rd Floor<br />
              Hartford, CT 06106<br />
              United States
            </Typography>
          </Section>

          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="body2" sx={{ color: '#7f8c8d', textAlign: 'center' }}>
              © 2025 Snackd, LLC. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50', mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export default TermsOfService;
